import { MtaAccessConfig, MtaAccessNotification, MtaBlockedReason } from '../../../../types';

/**
 * Determines MTA (Mid-Term Adjustment) access configuration based on environment and policy eligibility.
 *
 * @param activePolicy - The active policy status returned from e.g. the ICE Policy Management API call on the policy details page.
 * @param eligiblePolicy - The MTA eligibility status returned from the same call.
 * @param ineligibilityReason - The reason explaining why MTA access is not available when eligibility is false.
 * @param environmentEnabled - The (e.g. VITE_MTA_ENABLED) environment variable that controls whether MTA functionality is enabled. This is used to show service messages when needed.
 *
 * @returns An object containing:
 * - `permitted`: Boolean indicating if the user can access the MTA journey
 * - `notification`: Object with display logic for blocked access reasons
 *
 * @remarks
 * **IMPORTANT**: Developers should NOT add additional requirements to this function such as policy status checks,
 * renewal window validations, etc. All business logic for MTA eligibility is now backend-driven through the
 * Policy Management API.
 */
export const getMtaAccessConfig = (
	activePolicy: boolean,
	eligiblePolicy: boolean,
	ineligibilityReason: MtaBlockedReason,
	environmentEnabled: boolean,
): MtaAccessConfig => {
	// 1. Permission
	// To get on the MTA journey, we need to have an active policy, environment variable enabled, and be otherwise eligible
	const permitted: boolean = activePolicy && environmentEnabled && eligiblePolicy;

	// 2. Helper
	const showHelper = activePolicy && !permitted;
	let reasonBlocked: MtaBlockedReason;
	// Priority 1 - No reason is required if it's inactive
	if (activePolicy) {
		// Priority 2 - We need to tell a user if MTA is switched off
		if (!environmentEnabled) reasonBlocked = 'EnvDisabled';
		// Priority 3 - Show the ineligibility reason provided by policy management
		else reasonBlocked = ineligibilityReason;
	}
	const notification: MtaAccessNotification = {
		show: showHelper,
		reasonBlocked,
	};
	return {
		permitted,
		notification,
	};
};
